import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { Transaction } from '@asia/shared';
import { useTelegram } from './hooks/useTelegram';
import { useWalletGate } from './hooks/useWalletGate';
import { onEvent, onState } from './lib/ws';
import { setToken } from './lib/session';
import { clearReauth } from './lib/reauth-cache';
import { mergeTxsPage0 } from './lib/tx-cache';
import { writeBalanceCache } from './lib/balance-cache';
import type { TxPage } from './lib/tron';
import { useNav, type Route, type Tab } from './store/nav';
import { useBack } from './store/back';
import { useOnboarding } from './store/onboarding';
import { TelegramGate } from './components/TelegramGate';
import { Toast } from './components/Toast';
import { BottomNav } from './components/BottomNav';
import { WalletMainScreen } from './components/wallet/WalletMainScreen';
import { StubScreen } from './components/wallet/StubScreen';
import { SeedScreen } from './components/wallet/SeedScreen';
import { ProfileScreen } from './components/wallet/ProfileScreen';
import { EmailScreen } from './components/wallet/EmailScreen';
import { PinScreen } from './components/wallet/PinScreen';
import { BiometricScreen } from './components/wallet/BiometricScreen';
import { SupportScreen } from './components/wallet/SupportScreen';
import { FaqScreen } from './components/wallet/FaqScreen';
import { SessionsScreen } from './components/wallet/SessionsScreen';
import { ReferralScreen } from './components/wallet/ReferralScreen';
import { ReceiveScreen } from './components/wallet/ReceiveScreen';
import { SendScreen } from './components/wallet/SendScreen';
import { QrPayScreen } from './components/wallet/QrPayScreen';
import { CreateWalletScreen } from './components/wallet/CreateWalletScreen';
import { WalletsListScreen } from './components/wallet/WalletsListScreen';
import { Onboarding } from './components/onboarding/Onboarding';
import { AppLock } from './components/wallet/AppLock';
import { CoinInfoScreen } from './components/wallet/CoinInfoScreen';
import { CardsScreen } from './components/wallet/CardsScreen';
import { useWarmCardAccounts } from './hooks/useCardsApi';
import { BuySellScreen } from './components/wallet/BuySellScreen';

function TabView() {
  const tab = useNav((s) => s.tab);
  switch (tab) {
    case 'wallet':
      return <WalletMainScreen />;
    case 'cards':
      return <CardsScreen />;
    case 'profile':
      return <ProfileScreen />;
    default:
      return <WalletMainScreen />;
  }
}

function RouteView({ route }: { route: Route }) {
  if (route.name === 'seed') return <SeedScreen walletId={route.walletId} />;
  if (route.name === 'coin') return <CoinInfoScreen coin={route.coin} />;
  if (route.name === 'email') return <EmailScreen />;
  if (route.name === 'pin') return <PinScreen />;
  if (route.name === 'biometric') return <BiometricScreen />;
  if (route.name === 'support') return <SupportScreen />;
  if (route.name === 'faq') return <FaqScreen />;
  if (route.name === 'sessions') return <SessionsScreen />;
  if (route.name === 'referral') return <ReferralScreen />;
  if (route.name === 'receive') return <ReceiveScreen />;
  if (route.name === 'send') return <SendScreen coin={route.coin ?? 'USDT'} />;
  if (route.name === 'qr') return <QrPayScreen />;
  if (route.name === 'buy') return <BuySellScreen />;
  if (route.name === 'wallets') return <CreateWalletScreen />;
  if (route.name === 'walletList') return <WalletsListScreen />;
  const name = route.name;
  const titles: Record<string, string> = {
    send: 'Send',
    receive: 'Receive',
    buy: 'Buy',
    qr: 'QR payment',
    coin: 'Coin',
    history: 'History',
    wallets: 'My wallets',
    issueCard: 'Issue card',
    cardSettings: 'Card settings',
    faq: 'FAQ',
    support: 'Support',
  };
  return <StubScreen title={titles[name] ?? name} />;
}

const TAB_ORDER: Record<Tab, number> = { wallet: 0, cards: 1, profile: 2 };

/** WebSocket realtime: apply server pushes. Balances/txs updates land in the
 *  query cache; a `session_revoked` push (another device terminated this one) logs
 *  out instantly; on reconnect, profile + wallets + chain history are refetched. */
function useRealtime() {
  const qc = useQueryClient();
  useEffect(() => {
    const offBal = onEvent('balances', (d) => {
      const x = d as { address: string; trx: number; usdt: number };
      const bal = { trx: x.trx, usdt: x.usdt };
      qc.setQueryData(['balances', x.address], bal);
      writeBalanceCache(x.address, bal);
    });
    const offTxs = onEvent('txs', (d) => {
      // Apply the pushed page-0 list, keeping just-broadcast optimistic rows that
      // TronGrid has not indexed yet. A blind invalidate used to wipe those and
      // blank history until the indexer caught up (or forever for treasury pays).
      const x = d as { address: string; txs?: Transaction[] };
      if (x.txs) {
        // chain-watch pushes getTxs(..., 30); match that page size for the cursor.
        const nextBefore =
          x.txs.length >= 30 ? Date.parse(x.txs[x.txs.length - 1]!.date) || null : null;
        qc.setQueryData<InfiniteData<TxPage>>(['txs', x.address], (old) =>
          mergeTxsPage0(old, x.txs!, nextBefore),
        );
        return;
      }
      void qc.invalidateQueries({ queryKey: ['txs', x.address] });
    });
    // Card events all arrive from the provider's webhook: issuance finishing, a
    // purchase, a top-up landing, a 3DS challenge. Any of them can change the
    // list, so they share one refresh.
    const offCards = (
      [
        'card_issued',
        'card_updated',
        'card_balance',
        'card_topped_up',
        'card_transaction',
        'card_challenge',
        'card_issue_failed',
        'card_topup_failed',
      ] as const
    ).map((ev) =>
      onEvent(ev, (d) => {
        void qc.invalidateQueries({ queryKey: ['cards'] });
        void qc.invalidateQueries({ queryKey: ['cardIssueStatus'] });
        const cardId = (d as { cardId?: string } | null)?.cardId;
        if (cardId) void qc.invalidateQueries({ queryKey: ['cardTx', cardId] });
      }),
    );
    // A purchase settling at the on-ramp partner: refresh the order list, and
    // the wallet itself once the coins are on their way.
    const offOnramp = (['onramp_updated', 'onramp_completed'] as const).map((ev) =>
      onEvent(ev, () => {
        void qc.invalidateQueries({ queryKey: ['onrampOrders'] });
        void qc.invalidateQueries({ queryKey: ['onrampOrder'] });
        void qc.invalidateQueries({ queryKey: ['wallets'] });
      }),
    );
    const offRevoke = onEvent('session_revoked', () => {
      setToken(null);
      clearReauth();
      qc.clear();
      useOnboarding.getState().reset();
    });
    // Reconnect (network blip) → refresh profile, wallets, and chain history.
    // Skip the first authed event (initial connect — those queries run on their own).
    let first = true;
    const offState = onState((s) => {
      if (s !== 'authed') return;
      if (first) {
        first = false;
        return;
      }
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['wallets'] });
      void qc.invalidateQueries({ queryKey: ['cards'] });
      void qc.invalidateQueries({ queryKey: ['cardIssueStatus'] });
      void qc.invalidateQueries({ queryKey: ['txs'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
    });
    return () => {
      offBal();
      offTxs();
      for (const off of offCards) off();
      for (const off of offOnramp) off();
      offRevoke();
      offState();
    };
  }, [qc]);
}

/** A notification tapped in the chat can name where it wants to go
 *  (`?startapp=cards`). Honour it once, at launch. */
function useStartTab() {
  const setTab = useNav((s) => s.setTab);
  useEffect(() => {
    const param = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (param === 'cards' || param === 'profile' || param === 'wallet') setTab(param);
  }, [setTab]);
}

export function App() {
  useTelegram();
  useRealtime();
  useWarmCardAccounts();
  useStartTab();
  const stack = useNav((s) => s.stack);
  const tab = useNav((s) => s.tab);
  const top = stack.length ? stack[stack.length - 1]! : null;

  // Slide tabs left/right by travel direction (Telegram-style). The direction is
  // fixed once per tab change — computed in render only when `tab` actually
  // differs. Deriving it every render (with the swap in an effect) flipped it on
  // any re-render that landed mid-animation, restarting the slide in the opposite
  // direction ("the page slides right-left again"). Committing prevTab here keeps
  // tabDir stable across those re-renders.
  const prevTab = useRef(tab);
  const dirRef = useRef<'slide-in-right' | 'slide-in-left'>('slide-in-right');
  if (tab !== prevTab.current) {
    dirRef.current = TAB_ORDER[tab] >= TAB_ORDER[prevTab.current] ? 'slide-in-right' : 'slide-in-left';
    prevTab.current = tab;
  }
  const tabDir = dirRef.current;

  // Each tab / pushed screen starts at the top — don't inherit the previous
  // screen's scroll position (the window is the scroll container).
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, [tab, stack.length]);
  // Onboarding is decided by wallet existence (server): a user with a wallet
  // skips it; a new user sees it (and gets a wallet created on completion).
  const gate = useWalletGate();
  const completeOnboarding = useOnboarding((s) => s.complete);
  const qc = useQueryClient();

  // Native Telegram BackButton: a topmost registered handler (open overlay or an
  // internal step) takes precedence; otherwise it pops the route stack.
  const handlerCount = useBack((s) => s.handlers.length);

  // Bind the click once — it reads the latest state so it never needs rebinding.
  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (!bb) return;
    const onBack = () => {
      const handlers = useBack.getState().handlers;
      if (handlers.length) {
        handlers[handlers.length - 1]!.fn();
        return;
      }
      if (useNav.getState().stack.length) useNav.getState().pop();
    };
    bb.onClick(onBack);
    return () => bb.offClick(onBack);
  }, []);

  // Show the BackButton whenever there is anything to go back from.
  const backVisible = handlerCount > 0 || stack.length > 0;
  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (!bb) return;
    if (backVisible) bb.show();
    else bb.hide();
  }, [backVisible]);

  return (
    <TelegramGate>
      {gate === 'loading' ? (
        <div className="flex min-h-screen items-center justify-center bg-surface">
          <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        </div>
      ) : gate === 'onboarding' ? (
        <Onboarding
          onDone={() => {
            completeOnboarding();
            // A wallet was just created — re-evaluate the gate so we enter the app.
            void qc.invalidateQueries({ queryKey: ['wallets'] });
          }}
        />
      ) : (
        <AppLock>
          <div className="relative mx-auto min-h-full w-full max-w-[440px] overflow-x-hidden bg-surface">
            <div className="min-h-screen pt-safe">
              {top ? (
                <RouteView route={top} />
              ) : (
                <div key={tab} className={tabDir}>
                  <TabView />
                </div>
              )}
            </div>
            {!top && <BottomNav />}
          </div>
        </AppLock>
      )}
      <Toast />
    </TelegramGate>
  );
}
